import type { AdminFoodRequestDetail } from '@steado/admin-contract/foods';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircleIcon, CheckIcon, MinusIcon, RotateCcwIcon, TriangleAlertIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api/api-error';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { invalidateCatalog } from '@/screens/catalog-history/catalog-history-query';
import { BeforeAfter, Figures } from '@/screens/catalog-history/history-parts';

import {
  chosenMacros,
  chosenPortions,
  fixDecisions,
  fixRows,
  fixStatus,
  macroGap,
  macroKcal,
  valueText,
  type Choice,
  type FixRow,
} from './fix-model';
import { approveFix, invalidateRequests, rejectFoodRequest, researchAgain } from './food-requests-query';
import { ORIGIN_FROM, SOURCE_NAMES, ago, clock, confidenceOf, figure, plural } from './request-copy';
import {
  ADMIN_ONLY,
  Card,
  ConfidenceBadge,
  ConfidenceDot,
  KeyHints,
  KindBadge,
  RejectForm,
  ResearchForm,
  SourceRefs,
  SourcesPanel,
} from './review-parts';

const SOURCE_PREFIX = 'fix-source';

export interface ReviewProps {
  readonly request: AdminFoodRequestDetail;
  /** "1 of 3": where this request sits among the open ones of its kind. */
  readonly position: string | null;
  /** The next open request of its kind, for "Skip for now". */
  readonly onSkip: () => void;
  readonly onAnswered: () => void;
}

const GRID =
  'grid grid-cols-[minmax(170px,1.1fr)_minmax(160px,1fr)_minmax(160px,1fr)_72px_104px_96px] items-center gap-3';

/**
 * A fix to a food people already log, decided value by value: Keep current or
 * Use new for every change the research proposes, nothing chosen for the
 * reviewer. Apply waits, and says why, until every value is decided, energy
 * and the macros agree, and the reviewer has compared with the food as it is
 * now when somebody changed it after the research.
 */
export function FixReview({ request, position, onSkip, onAnswered }: ReviewProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const food = request.food;
  const draft = request.draft;
  const rows = useMemo(() => (draft !== null && food !== null ? fixRows(draft, food) : []), [draft, food]);
  const [choices, setChoices] = useState<Readonly<Record<string, Choice>>>({});
  const [comparedAt, setComparedAt] = useState<string | null>(null);
  const [panel, setPanel] = useState<'none' | 'reject' | 'research'>('none');
  const whyId = useId();
  const name = food === null ? request.name : (food.displayName ?? food.name);
  const stale = request.changedSinceDraft.length > 0 && comparedAt === null;

  const refresh = () => {
    invalidateRequests(queryClient);
    invalidateCatalog(queryClient);
  };

  const apply = useMutation({
    mutationFn: () =>
      approveFix(api, request.id, {
        decisions: food === null ? {} : fixDecisions(rows, choices, food),
        ...(comparedAt === null ? {} : { base: comparedAt }),
      }),
    onSuccess: () => {
      refresh();
      onAnswered();
    },
    onError: (failure) => {
      // Someone changed it after the reviewer compared: compare again before anything is written.
      if (failure instanceof ApiError && failure.status === 409) {
        setComparedAt(null);
        refresh();
      }
    },
    retry: false,
  });

  const reject = useMutation({
    mutationFn: (reason: string) => rejectFoodRequest(api, request.id, reason),
    onSuccess: () => {
      refresh();
      onAnswered();
    },
    retry: false,
  });

  const research = useMutation({
    mutationFn: (note: string) => researchAgain(api, request.id, note),
    onSuccess: () => {
      setPanel('none');
      refresh();
    },
    retry: false,
  });

  const status = food === null ? null : fixStatus(rows, choices, food, stale);
  const why = !can.mutate ? ADMIN_ONLY : (status?.why ?? null);

  // K keeps the first open value, U uses its new one, Enter applies: the reviewer's hands stay on the keys.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || panel !== 'none') return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [role="dialog"]') != null) return;
      const key = event.key.toLowerCase();
      const first = rows.find((row) => choices[row.id] === undefined);
      if ((key === 'k' || key === 'u') && first !== undefined) {
        event.preventDefault();
        setChoices((current) => ({ ...current, [first.id]: key === 'k' ? 'keep' : 'new' }));
      } else if (
        key === 'enter' &&
        target?.closest('button, a') == null &&
        why === null &&
        !status?.nothingChanges
      ) {
        event.preventDefault();
        apply.mutate();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [rows, choices, panel, why, status?.nothingChanges, apply]);

  const currentDefault = food?.portions.find((portion) => portion.isDefault) ?? food?.portions[0];
  const chosen = food === null ? null : chosenMacros(rows, choices, food);
  const chosenDefault =
    food === null ? undefined : chosenPortions(rows, choices, food).find((portion) => portion.isDefault);
  const allNew =
    food === null
      ? null
      : {
          macros: chosenMacros(rows, Object.fromEntries(rows.map((row) => [row.id, 'new' as const])), food),
          portion: chosenPortions(
            rows,
            Object.fromEntries(rows.map((row) => [row.id, 'new' as const])),
            food,
          ).find((portion) => portion.isDefault),
        };
  const kcalOf = (kcal: number, grams: number | undefined) =>
    grams === undefined ? null : Math.round((kcal * grams) / 100);
  const nowKcal = food === null ? null : kcalOf(food.macrosPer100g.kcal, currentDefault?.grams);
  const newKcal = allNew === null ? null : kcalOf(allNew.macros.kcal, allNew.portion?.grams);
  const unitLabel = currentDefault?.label ?? request.unit ?? 'portion';

  const taken = rows.filter((row) => choices[row.id] === 'new');
  const citedSources = [...new Set(taken.flatMap((row) => row.field?.sources ?? []))].sort((a, b) => a - b);
  const pendingBig = rows.filter(
    (row) => choices[row.id] === undefined && (row.id === 'kcal' || row.portion?.isDefault === true),
  );

  return (
    <div className="mx-auto flex w-full max-w-[1300px] flex-col gap-3.5">
      <p className="text-xs text-muted-foreground">Fix request{position === null ? '' : ` · ${position}`}</p>

      <section
        aria-labelledby="food-title"
        className="overflow-hidden rounded-xl border border-border bg-card"
      >
        <div className="flex flex-wrap items-start gap-3.5 px-[18px] pt-4 pb-3.5">
          <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h1 id="food-title" className="text-[17px] font-semibold tracking-[-0.01em] text-foreground">
                {name}
              </h1>
              <KindBadge kind="fix" />
              <ConfidenceBadge level={confidenceOf(request)} />
            </div>
            <p className="text-xs text-muted-foreground">
              {food === null
                ? 'The food this fix is about has been removed'
                : `${SOURCE_NAMES[food.source]}${food.isVerified ? ', verified' : ''} · ${food.slug}`}{' '}
              · {plural(request.userCount, 'person', 'people')} {ORIGIN_FROM[request.origin]}
              {request.researchedAt === null
                ? ' · not researched yet'
                : ` · researched ${ago(request.researchedAt)} from ${plural(request.sources.length, 'source')}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
              aria-expanded={panel === 'reject'}
              onClick={() => {
                setPanel(panel === 'reject' ? 'none' : 'reject');
              }}
            >
              Reject
            </Button>
            <Button
              variant="outline"
              aria-expanded={panel === 'research'}
              onClick={() => {
                setPanel(panel === 'research' ? 'none' : 'research');
              }}
            >
              <RotateCcwIcon aria-hidden="true" className="size-3.5" />
              Research again
            </Button>
          </div>
        </div>
        {panel === 'none' ? null : (
          <div className="border-t border-border bg-secondary/40 px-[18px] py-3.5">
            {panel === 'reject' ? (
              <RejectForm
                canMutate={can.mutate}
                isPending={reject.isPending}
                error={reject.isError ? reject.error.message : null}
                onReject={(reason) => {
                  reject.mutate(reason);
                }}
                onCancel={() => {
                  setPanel('none');
                }}
              />
            ) : (
              <ResearchForm
                canMutate={can.mutate}
                isPending={research.isPending}
                error={research.isError ? research.error.message : null}
                placeholder={`Look for ${name} as sold in India, by the piece.`}
                onResearch={(note) => {
                  research.mutate(note);
                }}
              />
            )}
          </div>
        )}
        {status === null || draft === null ? null : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] border-t border-border [&>*:last-child]:border-r-0">
            <Stat value={String(rows.length)} label="values proposed" />
            <Stat
              value={String(status.open.length)}
              label="left to decide"
              tone={status.open.length === 0 ? 'ok' : 'warn'}
            />
            <Stat value={String(request.touches?.logs90d ?? 0)} label="logs in 90 days" />
            <Stat value={String(request.touches?.people90d ?? 0)} label="people logged it" />
            <Stat
              value={
                nowKcal === null || newKcal === null ? 'None' : `${String(nowKcal)} → ${String(newKcal)}`
              }
              label={`kcal for 1 ${allNew?.portion?.label ?? unitLabel}, all new`}
            />
          </div>
        )}
      </section>

      {request.changedSinceDraft.length > 0 && stale ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-err-fg/25 bg-err-bg py-2.5 pr-2.5 pl-3.5"
        >
          <AlertCircleIcon aria-hidden="true" className="size-[15px] shrink-0 text-err-fg" />
          <span className="flex min-w-0 flex-[1_1_360px] flex-col gap-0.5">
            <span className="font-medium text-foreground">
              {name} changed since this was drafted: {changedFields(request)}.
            </span>
            <span className="text-xs text-muted-foreground">
              Changed at {clock(request.changedSinceDraft[0]?.changedAt ?? request.updatedAt)}. The table
              shows {name} as it is now. Applying waits until you compare.
            </span>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setComparedAt(new Date().toISOString());
              refresh();
            }}
          >
            <RotateCcwIcon aria-hidden="true" className="size-3.5" />
            Compare with {name} now
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-stretch gap-3.5">
        <Card title="Why this opened" className="flex-[3_1_460px]">
          <div className="flex flex-col gap-0.5 border-t border-border px-[18px] py-2.5">
            <span className="font-medium text-foreground">
              {plural(request.userCount, 'person', 'people')} {whyPhrase(request)}
            </span>
            <span className="text-[13px] text-foreground">
              {request.asks
                .slice(0, 4)
                .map((ask) => `“${ask.said}”`)
                .join(' · ')}
            </span>
          </div>
          {draft === null || draft.reviewerNotes.length === 0 ? null : (
            <div className="flex flex-col gap-0.5 border-t border-border px-[18px] pt-2.5 pb-3">
              <span className="font-medium text-foreground">The AI’s own notes</span>
              <ul className="flex max-w-[70ch] flex-col gap-0.5 text-[13px] text-muted-foreground">
                {draft.reviewerNotes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        {food === null ? null : (
          <Card title={`What someone logging 1 ${unitLabel} sees`} className="flex-[2_1_340px]">
            <SeesRow
              when="Now"
              name={name}
              portion={
                currentDefault === undefined
                  ? 'no portion yet'
                  : `1 ${currentDefault.label}, ${figure(currentDefault.grams)} g`
              }
              kcal={nowKcal}
              muted
            />
            <SeesRow
              when="With your choices"
              name={name}
              portion={
                chosenDefault === undefined
                  ? 'no portion yet'
                  : `1 ${chosenDefault.label}, ${figure(chosenDefault.grams)} g`
              }
              kcal={chosen === null ? null : kcalOf(chosen.kcal, chosenDefault?.grams)}
            />
            {pendingBig.length === 0 ? null : (
              <p className="border-t border-border px-[18px] pt-2.5 pb-3 text-xs text-muted-foreground">
                You have not decided{' '}
                {[
                  ...new Set(pendingBig.map((row) => (row.id === 'kcal' ? 'energy' : 'the default portion'))),
                ].join(' or ')}{' '}
                yet, so this still uses the current {pendingBig.length === 1 ? 'value' : 'values'}.
              </p>
            )}
          </Card>
        )}
      </div>

      {draft === null || food === null || status === null ? (
        <Card title="Not researched yet">
          <div className="flex flex-col gap-3 border-t border-border px-[18px] py-3.5">
            <p className="max-w-[70ch] text-[13px] text-muted-foreground">
              {request.draftStatus === 'queued'
                ? 'The research is running. This page shows its draft when its sources are in.'
                : request.draftStatus === 'failed'
                  ? `The last research found nothing to draft from${request.researchError === null ? '' : `: ${request.researchError}`}.`
                  : 'A fix is researched when someone asks for it. Research it to see what it would change, with the sources behind each value.'}
            </p>
            <ResearchForm
              canMutate={can.mutate}
              isPending={research.isPending}
              error={research.isError ? research.error.message : null}
              placeholder={`Look for ${name} as sold in India, by the piece.`}
              onResearch={(note) => {
                research.mutate(note);
              }}
            />
          </div>
        </Card>
      ) : (
        <>
          <section
            aria-labelledby="decide-title"
            className="overflow-hidden rounded-xl border border-warn-fg/25 bg-card"
          >
            <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 bg-warn-bg px-[18px] py-3">
              <h2 id="decide-title" className="text-[15px] font-semibold text-foreground">
                Decide each change
              </h2>
              <span className="text-xs text-muted-foreground">
                Nothing is chosen for you. Keep current leaves {name} as it is; Use new takes the researched
                value.
              </span>
              <span
                role="status"
                className={cn(
                  'ml-auto text-xs font-medium tabular-nums',
                  status.tone === 'ok'
                    ? 'text-ok-fg'
                    : status.tone === 'err'
                      ? 'text-err-fg'
                      : 'text-warn-fg',
                )}
              >
                {status.text}
              </span>
            </header>
            {rows.length === 0 ? (
              <p className="border-t border-border px-[18px] py-3 text-[13px] text-muted-foreground">
                The research proposes no change to {name}: it already has these values. Reject the request,
                saying why.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <div role="table" aria-label={`Proposed changes to ${name}`} className="min-w-[880px]">
                  <div
                    role="row"
                    className={cn(
                      GRID,
                      'border-t border-border bg-secondary px-[18px] py-2 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase',
                    )}
                  >
                    <span role="columnheader">Field</span>
                    <span role="columnheader">Keep current</span>
                    <span role="columnheader">Use new</span>
                    <span role="columnheader">Change</span>
                    <span role="columnheader">Confidence</span>
                    <span role="columnheader">Sources</span>
                  </div>
                  {rows.map((row) => (
                    <DecisionRow
                      key={row.id}
                      row={row}
                      choice={choices[row.id]}
                      stale={stale && changedRow(request, row)}
                      effect={effectOf(
                        row,
                        chosen?.kcal ?? food.macrosPer100g.kcal,
                        chosenDefault?.grams ?? currentDefault?.grams,
                        unitLabel,
                      )}
                      onChoose={(choice) => {
                        setChoices((current) => ({ ...current, [row.id]: choice }));
                      }}
                      energyNote={
                        row.id === 'kcal' ? (
                          <EnergyCheck
                            note={row.field?.note ?? null}
                            rows={rows}
                            choices={choices}
                            macros={chosen ?? food.macrosPer100g}
                          />
                        ) : null
                      }
                    />
                  ))}
                  <div
                    role="row"
                    className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border bg-secondary/40 px-[18px] py-2"
                  >
                    <span role="cell" className="flex-[1_1_400px] text-xs text-muted-foreground">
                      {unchangedLine(rows, food)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </section>

          <div className="flex flex-wrap items-start gap-3.5">
            <SourcesPanel
              title="Sources the AI read"
              sources={request.sources}
              idPrefix={SOURCE_PREFIX}
              className="min-w-0 flex-[13_1_480px]"
            />
            <Card title="Who this touches" className="flex-[10_1_380px]">
              <dl>
                <TouchRow label="Logs, last 90 days">
                  <Figures
                    text={`${String(request.touches?.logs90d ?? 0)} by ${plural(request.touches?.people90d ?? 0, 'person', 'people')}`}
                  />
                </TouchRow>
                <TouchRow label="Favourites">
                  <Figures text={plural(request.touches?.favourites ?? 0, 'person', 'people')} />
                </TouchRow>
                <TouchRow label={`The ${plural(request.userCount, 'person', 'people')} who asked`}>
                  See the fix the next time they log {name.toLowerCase()}
                </TouchRow>
                <TouchRow label="History">
                  One change by you, with its sources. Anyone can revert it.
                </TouchRow>
              </dl>
            </Card>
          </div>

          <section
            aria-labelledby="apply-title"
            className="overflow-hidden rounded-xl border border-border bg-card"
          >
            <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 px-[18px] pt-3.5 pb-3">
              <h2 id="apply-title" className="text-[15px] font-semibold text-foreground">
                What changes
              </h2>
              <span className="text-xs text-muted-foreground">Only the fields you set to Use new</span>
            </header>
            {taken.map((row) => (
              <div
                key={row.id}
                className="grid grid-cols-[minmax(160px,0.8fr)_minmax(0,1.2fr)] items-baseline gap-3 border-t border-border px-[18px] py-2.5 text-[13px]"
              >
                <span className="text-muted-foreground">{row.label}</span>
                <BeforeAfter
                  before={row.current === null ? 'none' : valueText(row, row.current)}
                  after={row.proposed === null ? 'removed' : valueText(row, row.proposed)}
                />
              </div>
            ))}
            {taken.length > 0 ? null : (
              <p className="border-t border-border px-[18px] py-2.5 text-[13px] text-muted-foreground">
                {status.nothingChanges
                  ? `Nothing changes: every field is set to Keep current. Close the request instead, saying why.`
                  : 'Nothing changes yet. Fields you set to Use new show here.'}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-border bg-secondary/40 px-[18px] py-3.5">
              <span className="flex min-w-0 flex-[1_1_380px] flex-col gap-1">
                <span
                  className={cn(
                    'font-medium tabular-nums',
                    status.tone === 'ok'
                      ? 'text-ok-fg'
                      : status.tone === 'err'
                        ? 'text-err-fg'
                        : 'text-warn-fg',
                  )}
                >
                  {status.text}
                </span>
                <span className="max-w-[70ch] text-xs text-muted-foreground">
                  Past logs keep the numbers they were saved with. New logs use the fix from now on.
                </span>
                <KeyHints
                  label="Keyboard: K keeps the current value, U uses the new value, Enter applies the fix"
                  keys={[
                    ['K', 'keep current'],
                    ['U', 'use new'],
                    ['Enter', 'apply'],
                  ]}
                />
                {citedSources.length === 0 ? null : (
                  <span className="text-xs text-muted-foreground">
                    Saved with {plural(citedSources.length, 'source')}{' '}
                    <SourceRefs indexes={citedSources} idPrefix={SOURCE_PREFIX} /> · goes into {name}’s
                    History
                  </span>
                )}
                {apply.isError ? (
                  <span role="alert" className="text-[12.5px] font-medium text-err-fg">
                    {apply.error.message}
                  </span>
                ) : null}
              </span>
              <Button variant="ghost" size="lg" className="text-muted-foreground" onClick={onSkip}>
                Skip for now
              </Button>
              {status.nothingChanges && why === null ? (
                <Button
                  size="lg"
                  variant="outline"
                  className="font-semibold"
                  onClick={() => {
                    setPanel('reject');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Close without changes
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    aria-disabled={why !== null || apply.isPending}
                    aria-describedby={why === null ? undefined : whyId}
                    className={cn(
                      'font-semibold',
                      why !== null &&
                        'cursor-not-allowed border border-border bg-muted text-muted-foreground shadow-none hover:bg-muted',
                    )}
                    onClick={() => {
                      if (why === null && !apply.isPending) apply.mutate();
                    }}
                  >
                    {apply.isPending ? 'Applying…' : `Apply fix to ${name}`}
                  </Button>
                  {why === null ? null : (
                    <span id={whyId} className="sr-only">
                      {why}
                    </span>
                  )}
                </>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({
  value,
  label,
  tone,
}: {
  readonly value: string;
  readonly label: string;
  readonly tone?: 'ok' | 'warn';
}) {
  return (
    <div className="flex flex-col gap-0.5 border-r border-border px-[18px] pt-2.5 pb-3">
      <span
        className={cn(
          'font-mono text-[13px] font-medium tabular-nums',
          tone === 'ok' ? 'text-ok-fg' : tone === 'warn' ? 'text-warn-fg' : 'text-foreground',
        )}
      >
        {value}
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function SeesRow({
  when,
  name,
  portion,
  kcal,
  muted = false,
}: {
  readonly when: string;
  readonly name: string;
  readonly portion: string;
  readonly kcal: number | null;
  readonly muted?: boolean;
}) {
  return (
    <div className="grid grid-cols-[124px_minmax(0,1fr)_auto] items-baseline gap-3 border-t border-border px-[18px] py-2.5">
      <span className="text-xs text-muted-foreground">{when}</span>
      <span className="flex flex-wrap gap-x-2 gap-y-0.5">
        <span className="font-medium text-foreground">{name}</span>
        <Figures text={portion} className="text-xs text-muted-foreground" />
      </span>
      <span className={cn('text-[13px]', !muted && 'font-medium')}>
        <span className={cn('font-mono tabular-nums', muted && 'text-muted-foreground')}>
          {kcal === null ? 'None' : String(kcal)}
        </span>{' '}
        kcal
      </span>
    </div>
  );
}

function TouchRow({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] items-baseline gap-3.5 border-t border-border px-[18px] py-2.5 text-[13px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{children}</dd>
    </div>
  );
}

/** The line under a choice: what energy or the default portion means for one portion. */
function effectOf(
  row: FixRow,
  chosenKcal: number,
  grams: number | undefined,
  unitLabel: string,
): { keep: string; take: string } | null {
  if (row.id === 'kcal' && grams !== undefined) {
    return {
      keep: `${String(Math.round(((row.current ?? 0) * grams) / 100))} kcal a ${figure(grams)} g ${unitLabel}`,
      take: `${String(Math.round(((row.proposed ?? 0) * grams) / 100))} kcal a ${figure(grams)} g ${unitLabel}`,
    };
  }
  if (row.portion?.isDefault === true) {
    const label = row.portion.label;
    return {
      keep:
        row.current === null ? '' : `${String(Math.round((chosenKcal * row.current) / 100))} kcal a ${label}`,
      take:
        row.proposed === null
          ? ''
          : `${String(Math.round((chosenKcal * row.proposed) / 100))} kcal a ${label}`,
    };
  }
  return null;
}

function DecisionRow({
  row,
  choice,
  stale,
  effect,
  onChoose,
  energyNote,
}: {
  readonly row: FixRow;
  readonly choice: Choice | undefined;
  readonly stale: boolean;
  readonly effect: { keep: string; take: string } | null;
  readonly onChoose: (choice: Choice) => void;
  readonly energyNote: ReactNode;
}) {
  return (
    <>
      <div role="row" className={cn(GRID, 'border-t border-border px-[18px] py-2.5')}>
        <span role="cell" className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium text-foreground">
          {row.label}
          {choice === undefined ? (
            <span className="inline-flex h-5 items-center rounded-[10px] bg-warn-bg px-1.5 text-xs font-medium text-warn-fg">
              Open
            </span>
          ) : null}
          {stale ? (
            <span className="inline-flex h-5 items-center rounded-[10px] bg-err-bg px-1.5 text-xs font-medium text-err-fg">
              Changed since drafted
            </span>
          ) : null}
        </span>
        <span role="cell">
          <ChoiceButton
            pressed={choice === 'keep'}
            heading="Keep current"
            value={row.current === null ? 'None' : valueText(row, row.current)}
            sub={[
              ...(row.drafted === null ? [] : [`${figure(row.drafted)} when drafted`]),
              ...(effect === null || effect.keep === '' ? [] : [effect.keep]),
            ]}
            label={`Keep current ${row.label}: ${row.current === null ? 'none' : valueText(row, row.current)}`}
            onClick={() => {
              onChoose('keep');
            }}
          />
        </span>
        <span role="cell">
          <ChoiceButton
            pressed={choice === 'new'}
            heading="Use new"
            value={row.proposed === null ? 'Removed' : valueText(row, row.proposed)}
            sub={effect === null || effect.take === '' ? [] : [effect.take]}
            label={`Use new ${row.label}: ${row.proposed === null ? 'removed' : valueText(row, row.proposed)}`}
            onClick={() => {
              onChoose('new');
            }}
          />
        </span>
        <span role="cell" className="font-mono text-[13px] text-muted-foreground tabular-nums">
          {row.change}
        </span>
        <span role="cell" className="text-[13px]">
          <ConfidenceDot level={row.field?.confidence ?? 'none'} className="font-normal" />
        </span>
        <span role="cell">
          <SourceRefs indexes={row.field?.sources ?? []} idPrefix={SOURCE_PREFIX} />
        </span>
      </div>
      {energyNote}
    </>
  );
}

/** Keep current or Use new: a pressed toggle, dark when chosen, as the design draws it. */
export function ChoiceButton({
  pressed,
  heading,
  value,
  sub,
  label,
  onClick,
}: {
  readonly pressed: boolean;
  readonly heading: string;
  readonly value: string;
  readonly sub: readonly string[];
  readonly label: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex min-h-10 w-full flex-col items-start gap-px rounded-lg border px-3 pt-1.5 pb-[7px] text-left text-[13px] leading-snug transition-colors',
        pressed
          ? 'border-foreground bg-foreground text-background'
          : 'border-input bg-card text-foreground hover:bg-accent',
      )}
    >
      <span className={cn('text-xs', pressed ? 'text-background/75' : 'text-muted-foreground')}>
        {heading}
      </span>
      <Figures text={value} className="font-medium" />
      {sub.map((line) => (
        <Figures
          key={line}
          text={line}
          className={cn('text-xs', pressed ? 'text-background/75' : 'text-muted-foreground')}
        />
      ))}
    </button>
  );
}

function EnergyCheck({
  note,
  rows,
  choices,
  macros,
}: {
  readonly note: string | null;
  readonly rows: readonly FixRow[];
  readonly choices: Readonly<Record<string, Choice>>;
  readonly macros: { kcal: number; proteinG: number; carbsG: number; fatG: number };
}) {
  const energy = rows.find((row) => row.id === 'kcal');
  const decidedMacros = ['proteinG', 'carbsG', 'fatG'].every(
    (key) => !rows.some((row) => row.id === key) || choices[key] !== undefined,
  );
  const sum = macroKcal(macros);
  const say = (kcal: number): string => {
    const gap = macroGap(kcal, macros);
    const pct = Math.abs(gap) * 100;
    if (pct < 1) return 'is within 1%.';
    return `is ${String(Math.round(pct))}% ${gap < 0 ? 'below' : 'above'} that${pct > 10 ? ': too far apart to apply.' : '.'}`;
  };
  const choice = energy === undefined ? undefined : choices[energy.id];
  const gap = choice === undefined ? null : Math.abs(macroGap(macros.kcal, macros));
  return (
    <div role="row" className="flex flex-wrap gap-x-7 gap-y-2.5 px-[18px] pb-3">
      <div role="cell" className="flex min-w-0 flex-[1_1_360px] flex-col gap-0.5">
        <span className="text-xs font-medium text-muted-foreground">AI’s note</span>
        <p className="max-w-[70ch] text-[13px] text-foreground">
          {note ?? 'The research left no note on energy.'}
        </p>
      </div>
      <div role="cell" className="flex min-w-0 flex-[1_1_360px] flex-col gap-0.5">
        <span className="text-xs font-medium text-muted-foreground">Console check</span>
        <span className="flex items-start gap-[7px] text-[13px]">
          {gap === null ? (
            <span
              role="img"
              aria-label="Not decided yet"
              className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-muted"
            >
              <MinusIcon aria-hidden="true" className="size-[11px] text-muted-foreground" />
            </span>
          ) : gap < 0.01 ? (
            <span
              role="img"
              aria-label="Passes"
              className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-ok-bg"
            >
              <CheckIcon aria-hidden="true" className="size-[11px] text-ok-fg" />
            </span>
          ) : (
            <span
              role="img"
              aria-label="Does not match"
              className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-warn-bg"
            >
              <TriangleAlertIcon aria-hidden="true" className="size-[11px] text-warn-fg" />
            </span>
          )}
          <span className="flex flex-col gap-px">
            <span>
              Protein, carbohydrate and fat {decidedMacros ? 'as you set them' : 'as set so far'} add up to{' '}
              <span className="font-mono tabular-nums">{sum.toFixed(1)}</span> kcal per 100 g.
            </span>
            <span className="font-mono text-muted-foreground tabular-nums">
              {macros.proteinG.toFixed(1)} × 4 + {macros.carbsG.toFixed(1)} × 4 + {macros.fatG.toFixed(1)} × 9
              = {sum.toFixed(1)}
            </span>
            {energy === undefined ? null : choice === undefined ? (
              <span>
                Use new <span className="font-mono tabular-nums">{figure(energy.proposed ?? 0)}</span>{' '}
                {say(energy.proposed ?? 0)} Keep current{' '}
                <span className="font-mono tabular-nums">{figure(energy.current ?? 0)}</span>{' '}
                {say(energy.current ?? 0)}
              </span>
            ) : (
              <span>
                Energy <span className="font-mono tabular-nums">{figure(macros.kcal)}</span>{' '}
                {say(macros.kcal)}
              </span>
            )}
          </span>
        </span>
      </div>
    </div>
  );
}

function changedNames(request: AdminFoodRequestDetail): Set<'nutrition' | 'portions'> {
  const groups = new Set<'nutrition' | 'portions'>();
  for (const change of request.changedSinceDraft) {
    if (/kcal|protein|carbs|fat|fibre/.test(change.field)) groups.add('nutrition');
    else groups.add('portions');
  }
  return groups;
}

const MACRO_COLUMNS: Readonly<Record<string, string>> = {
  kcal: 'kcal_per_100g',
  proteinG: 'protein_g_per_100g',
  carbsG: 'carbs_g_per_100g',
  fatG: 'fat_g_per_100g',
  fibreG: 'fibre_g_per_100g',
};

/** Whether a row's own value changed since the draft: its column for a figure, any portion for a portion. */
function changedRow(request: AdminFoodRequestDetail, row: FixRow): boolean {
  const column = MACRO_COLUMNS[row.id];
  return request.changedSinceDraft.some((change) =>
    column === undefined ? !Object.values(MACRO_COLUMNS).includes(change.field) : change.field === column,
  );
}

function changedFields(request: AdminFoodRequestDetail): string {
  const groups = changedNames(request);
  return [...groups]
    .map((group) => (group === 'nutrition' ? 'its nutrition changed' : 'its portions changed'))
    .join(' and ');
}

function whyPhrase(request: AdminFoodRequestDetail): string {
  if (request.unit !== null) {
    return `asked for 1 ${request.unit} of ${request.foodName ?? request.name}, which has no ${request.unit} portion`;
  }
  return `${ORIGIN_FROM[request.origin]} said its numbers look wrong`;
}

function unchangedLine(rows: readonly FixRow[], food: NonNullable<AdminFoodRequestDetail['food']>): string {
  const touched = new Set(rows.map((row) => row.id));
  const kept = [
    ...(touched.has('kcal') ? [] : [`energy ${figure(food.macrosPer100g.kcal)} kcal`]),
    ...(touched.has('proteinG') ? [] : [`protein ${figure(food.macrosPer100g.proteinG)} g`]),
    ...(touched.has('carbsG') ? [] : [`carbohydrate ${figure(food.macrosPer100g.carbsG)} g`]),
    ...(touched.has('fatG') ? [] : [`fat ${figure(food.macrosPer100g.fatG)} g`]),
    ...(touched.has('fibreG') || food.macrosPer100g.fibreG === null
      ? []
      : [`fibre ${figure(food.macrosPer100g.fibreG)} g`]),
  ];
  const what = [...kept, 'names', 'diet class', 'allergens'];
  return `Everything else stays as it is: ${what.join(', ')}.`;
}
